import React, { FC, useRef, useState, useEffect } from 'react'
import cn from 'classnames'
import Link from 'next/link'
import { useRouter } from 'next/router'
import s from '@corratech/air-ooka-header/DropdownMenu/DropdownMenu.module.scss'
import { User } from '@components/icons'
import { useUI } from '@corratech/pylot-ui/context'
import { ClickOutside } from '@corratech/pylot-utils'
import { useTranslation } from 'next-i18next'
import { useUser } from '@corratech/pylot-auth-manager'
import { useStoreConfig } from '@config/hooks/useStoreConfig'

import {
    disableBodyScroll,
    enableBodyScroll,
    clearAllBodyScrollLocks
} from 'body-scroll-lock'

interface DropdownMenuProps {
    open?: boolean
    logout: () => Promise<void>
}

const DropdownMenu: FC<DropdownMenuProps> = ({ logout }) => {
    const { t } = useTranslation('common')
    const { pathname } = useRouter()
    const [display, setDisplay] = useState(false)
    const { closeSidebarIfPresent } = useUI()
    const ref = useRef() as React.MutableRefObject<HTMLUListElement>
    const { customer } = useUser()
    const config = useStoreConfig()
    const LINKS = [
        {
            name: `${t('My Account')}`,
            href: '/account',
            enable: true
        },
        {
            name: `${t('My Wish List')}`,
            href: '/account/wishlist',
            enable: config?.base?.wishlist?.enabled
        },
        {
            name: `${t('My Subscriptions')}`,
            href: '/account/subscription-profile',
            enable: false
        },
        {
            name: `${t('My Orders')}`,
            href: '/account/orders',
            enable: true
        }
    ]

    useEffect(() => {
        if (ref.current) {
            if (display) {
                disableBodyScroll(ref.current)
            } else {
                enableBodyScroll(ref.current)
            }
        }
        return () => {
            clearAllBodyScrollLocks()
        }
    }, [display])

    return (
        <ClickOutside active={display} onClick={() => setDisplay(false)}>
            <div className={s['dropdownMenu']}>
                <button
                    className={s.avatarButton}
                    onClick={() => setDisplay(!display)}
                    aria-label={t('auth|Menu')}
                >
                    <User />
                </button>
                <ul
                    className={cn(
                        s['dropdownMenuPopup'],
                        display && s.dropdownMenuPopupOpen
                    )}
                    ref={ref}
                >
                    <li className={s['WelcomeMessage']}>
                        {`${t('Hi {{name}}', {
                            name: customer?.firstname
                        })}`}
                    </li>
                    {LINKS.map(({ name, href, enable }) => (
                        <>
                            {enable && (
                                <li key={name + href} className={s['userLink']}>
                                    <Link href={href} passHref>
                                        <span
                                            className={cn(s.link, {
                                                [s.active]: pathname === href
                                            })}
                                            onClick={() => {
                                                setDisplay(false)
                                                closeSidebarIfPresent()
                                            }}
                                            aria-hidden="true"
                                        >
                                            {name}
                                        </span>
                                    </Link>
                                </li>
                            )}
                        </>
                    ))}
                    <li className={s.userSignOut}>
                        <span
                            onClick={() => {
                                setDisplay(false)
                                logout()
                            }}
                            aria-hidden="true"
                        >
                            {t('sign out')}
                        </span>
                    </li>
                </ul>
            </div>
        </ClickOutside>
    )
}

export default DropdownMenu
